import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, ChevronRight, BookOpen, List, Settings } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useChat } from '../context/ChatContext';
import './CourseExpand.css';

const TYPE_CONFIG = {
  prerequisite: {
    icon: BookOpen,
    labelKey: 'chat:course.curriculum.prerequisite',
    color: '#8B5CF6',
  },
  curriculum: {
    icon: List,
    labelKey: 'chat:course.curriculum.main',
    color: '#3498db',
  },
  configuration: {
    icon: Settings,
    labelKey: 'chat:course.curriculum.configuration',
    color: '#10b981',
  },
};

/**
 * Parse curriculum content
 * Format:
 * 0.1 Topic: Description
 * 1.2 Topic: Description
 * or just plain text for configuration
 */
function parseContent(content, type) {
  if (type === 'configuration') {
    // For configuration, just return raw lines
    return content.split('\n').map(line => line.trim()).filter(line => line.length > 0);
  }

  // For prerequisite/curriculum, parse numbered items
  const lines = content.split('\n').map(line => line.trim()).filter(line => line.length > 0);
  
  return lines.map(line => {
    const match = line.match(/^(\d+\.\d+)\s+(.+?):\s*(.+)$/);
    if (match) {
      return {
        number: match[1],
        topic: match[2],
        description: match[3],
      };
    }
    // Fallback for lines without description
    const simpleMatch = line.match(/^(\d+\.\d+)\s+(.+)$/);
    if (simpleMatch) {
      return {
        number: simpleMatch[1],
        topic: simpleMatch[2],
        description: '',
      };
    }
    // Fallback for plain text
    return { text: line };
  }).filter(item => item !== null);
}

/**
 * Curriculum item component with hover START button
 */
function CurriculumItem({ item, onStart, sending, disabled = false }) {
  const [isHovered, setIsHovered] = useState(false);

  // Build the full line text for /start command
  const getLineText = () => {
    if (item.number) {
      let text = `${item.number} ${item.topic}`;
      if (item.description) {
        text += `: ${item.description}`;
      }
      return text;
    }
    return item.text || '';
  };

  const handleStart = (e) => {
    e.stopPropagation();
    if (!sending && !disabled) {
      onStart(`/start ${getLineText()}`);
    }
  };

  return (
    <li
      className={`course-expand-item ${isHovered ? 'hovered' : ''}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {item.number ? (
        <>
          <span className="course-expand-number">{item.number}</span>
          <div className="course-expand-text">
            <strong>{item.topic}</strong>
            {item.description && (
              <span className="course-expand-description">: {item.description}</span>
            )}
          </div>
        </>
      ) : (
        <div className="course-expand-text">{item.text}</div>
      )}
      {isHovered && !disabled && (
        <button
          className="course-expand-start-btn"
          onClick={handleStart}
          disabled={sending}
        >
          START
        </button>
      )}
    </li>
  );
}

/**
 * Generic collapsible course block component
 * Handles prerequisite, curriculum, and configuration blocks
 * Only Main Curriculum uses sticky behavior (IntersectionObserver + Portal)
 * @param {boolean} disabled - When true, START buttons are hidden (for shared view)
 */
export default function CourseExpand({ content, type = 'curriculum', disabled = false }) {
  const { t } = useTranslation();
  // Independent expand states for embed (in-flow) vs sticky (portal) versions
  // Main curriculum: embed starts expanded, sticky starts collapsed
  // Prerequisite/configuration: embed starts collapsed (no sticky version)
  const [embedExpanded, setEmbedExpanded] = useState(type === 'curriculum');
  const [stickyExpanded, setStickyExpanded] = useState(false);
  const [isSticky, setIsSticky] = useState(false);
  const [stickyStyle, setStickyStyle] = useState({});
  const containerRef = useRef(null);
  const sentinelRef = useRef(null);
  const dimensionsRef = useRef({ width: 0, left: 0, height: 0 });
  const { sendMessage, sending } = useChat();
  const config = TYPE_CONFIG[type] || TYPE_CONFIG.curriculum;
  const Icon = config.icon;
  const items = parseContent(content, type);

  // Only Main Curriculum should be sticky (keep sticky even in disabled/shared view)
  const shouldBeSticky = type === 'curriculum';

  // Find the scroll container (.messages-area)
  const getScrollContainer = () => {
    if (!sentinelRef.current) return null;
    return sentinelRef.current.closest('.messages-area');
  };

  // Update sticky position based on scroll container
  const updateStickyPosition = () => {
    const scrollContainer = getScrollContainer();
    if (!scrollContainer) return;

    const containerRect = scrollContainer.getBoundingClientRect();

    setStickyStyle({
      position: 'fixed',
      top: containerRect.top, // At top of scroll container
      left: dimensionsRef.current.left,
      width: dimensionsRef.current.width,
      zIndex: 100,
    });
  };

  // Use IntersectionObserver to detect when element should become sticky
  // Only for Main Curriculum type
  useEffect(() => {
    if (!shouldBeSticky) return; // Skip for non-curriculum types

    const scrollContainer = getScrollContainer();
    if (!scrollContainer || !sentinelRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const nowSticky = !entry.isIntersecting;

          // When becoming sticky, save dimensions
          if (nowSticky && !isSticky && containerRef.current) {
            const rect = containerRef.current.getBoundingClientRect();
            dimensionsRef.current = {
              width: rect.width,
              left: rect.left,
              height: rect.height,
            };
            updateStickyPosition();
          }

          setIsSticky(nowSticky);
        });
      },
      {
        root: scrollContainer,
        // Trigger when element reaches top of scroll container
        rootMargin: '0px 0px 0px 0px',
        threshold: 0,
      }
    );

    observer.observe(sentinelRef.current);

    // Update position on window resize
    const handleResize = () => {
      if (isSticky && containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        dimensionsRef.current = {
          width: rect.width,
          left: rect.left,
          height: rect.height,
        };
        updateStickyPosition();
      }
    };
    window.addEventListener('resize', handleResize);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', handleResize);
    };
  }, [isSticky, shouldBeSticky]);

  // Update position when sticky state changes
  useEffect(() => {
    if (isSticky) {
      updateStickyPosition();
    }
  }, [isSticky]);

  // Render content with specific expand state
  // @param {boolean} expanded - Current expanded state
  // @param {function} setExpanded - Function to toggle expanded state
  const renderContent = (expanded, setExpanded) => (
    <div className="course-expand-container">
      <button
        className="course-expand-header"
        onClick={() => setExpanded(!expanded)}
        aria-expanded={expanded}
        style={{ '--header-color': config.color }}
      >
        <div className="course-expand-title">
          <Icon size={16} className="course-expand-icon" />
          <span className="course-expand-label">{t(config.labelKey)}</span>
          {type !== 'configuration' && (
            <span className="course-expand-count">({items.length} topics)</span>
          )}
        </div>
        {expanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
      </button>

      {expanded && (
        <div className="course-expand-content">
          {type === 'configuration' ? (
            <div className="course-expand-config">
              {items.map((line, index) => (
                <div key={index} className="course-expand-config-line">
                  {line}
                </div>
              ))}
            </div>
          ) : (
            <ol className="course-expand-list">
              {items.map((item, index) => (
                <CurriculumItem
                  key={index}
                  item={item}
                  onStart={sendMessage}
                  sending={sending}
                  disabled={disabled}
                />
              ))}
            </ol>
          )}
        </div>
      )}
    </div>
  );

  // For non-curriculum types, render without sticky behavior
  // Use embedExpanded state for the in-flow version
  if (!shouldBeSticky) {
    return (
      <div ref={containerRef}>
        {renderContent(embedExpanded, setEmbedExpanded)}
      </div>
    );
  }

  // Curriculum type with sticky behavior
  // Embed version uses embedExpanded, sticky portal uses stickyExpanded
  return (
    <>
      {/* Sentinel element to detect scroll position */}
      <div ref={sentinelRef} className="course-expand-sentinel" />

      {/* Original element - keeps its place in flow, uses embedExpanded state */}
      <div
        ref={containerRef}
        style={isSticky ? { visibility: 'hidden' } : undefined}
      >
        {renderContent(embedExpanded, setEmbedExpanded)}
      </div>

      {/* Sticky portal - positioned fixed when active, uses stickyExpanded state */}
      {isSticky && createPortal(
        <div className="course-expand-container is-sticky-portal" style={stickyStyle}>
          <button
            className="course-expand-header"
            onClick={() => setStickyExpanded(!stickyExpanded)}
            aria-expanded={stickyExpanded}
            style={{ '--header-color': config.color }}
          >
            <div className="course-expand-title">
              <Icon size={16} className="course-expand-icon" />
              <span className="course-expand-label">{t(config.labelKey)}</span>
              <span className="course-expand-count">({items.length} topics)</span>
            </div>
            {stickyExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
          </button>

          {stickyExpanded && (
            <div className="course-expand-content">
              <ol className="course-expand-list">
                {items.map((item, index) => (
                  <CurriculumItem
                    key={index}
                    item={item}
                    onStart={sendMessage}
                    sending={sending}
                  />
                ))}
              </ol>
            </div>
          )}
        </div>,
        document.body
      )}
    </>
  );
}

